/**
 * Maps short URL query parameter keys to their corresponding long filter keys
 * used internally by the application, plus the special `search` key.
 */
const KEY_MAP: Record<string, keyof Filters | 'search'> = {
  t: 'type',
  c: 'country',
  uc: 'use_case',
  th: 'theme',
  tc: 'technology',
  bm: 'business_model',
  m: 'maturity',
  f: 'fvc',
  u: 'users',
  ot: 'organization_type',
  oc: 'origin_country',
  s: 'sdg',
  fb: 'four_betters',
  q: 'search'
};

const REVERSE_KEY_MAP = Object.entries(KEY_MAP).reduce((accumulator, [short, long]) => {
  accumulator[long as string] = short;
  return accumulator;
}, {} as Record<string, string>);

/**
 * Serializes the current filters state and search term into a URL query string.
 *
 * Example output:
 *   "t=Good+practice&oc=Belgium&q=SomethingWroteInTheSearchBar"
 */
export const encodeFiltersToURL = (filters: Filters, searchTerm: string) => {
  const params = new URLSearchParams();

  if (searchTerm) params.set('q', searchTerm);

  Object.entries(filters).forEach(([key, value]) => {
    const shortKey = REVERSE_KEY_MAP[key];
    if (shortKey && Array.isArray(value) && value.length > 0) {
      params.set(shortKey, value.join(','));
    }
  });
  return params.toString();
};

/**
 * Returns:
 * - {
 *     filters: Filters,
 *     search: string
 *   }
 *
 * Example:
 *   Input: "?t=Good+practice&oc=Belgium&q=something"
 *   Output:
 *     {
 *       filters: { type: ['good practice'], country: ['Belgium'], ... },
 *       search: "something"
 *     }
 */
export const decodeURLToFilters = (searchString: string, initialFilters: Filters): { filters: Filters, search: string } => {
  const params = new URLSearchParams(searchString);
  const newFilters = { ...initialFilters };
  let newSearch = '';

  params.forEach((value, key) => {
    if (key === 'q') {
      newSearch = value;
      return;
    }
    const longKey = KEY_MAP[key];
    if (longKey) {
      // Split by comma and filter empty strings
      newFilters[longKey] = value.split(',').filter(Boolean);
    }
  });

  return { filters: newFilters, search: newSearch };
};